import React from "react";
import { Button, Collapse, Form, Input, Radio, Select, Spin, Table, Tooltip } from "antd";
import { FileExcelOutlined } from "@ant-design/icons";
import { CSVLink } from "react-csv";
import { useLazyFetchInventoryValuationReportQuery } from "features/store";

const { Panel } = Collapse;

const MATERIAL_TYPES = ["CLOTH MATERIAL", "CAP MATERIAL", "OTHER MATERIAL"];

const columns = [
  { title: "Material Name", dataIndex: "material_name", key: "material_name" },
  { title: "Color", dataIndex: "color", key: "color" },
  { title: "Sub Type", dataIndex: "sub_type", key: "sub_type" },
  { title: "Current Qty", dataIndex: "current_quantity", key: "current_quantity" },
  { title: "Unit", dataIndex: "unit", key: "unit" },
  { title: "Rate Used", dataIndex: "rate_used", key: "rate_used" },
  { title: "Rate Type", dataIndex: "rate_type_label", key: "rate_type_label" },
  {
    title: "Valuation (₹)",
    dataIndex: "valuation",
    key: "valuation",
    render: (val) => (val != null ? `₹${Number(val).toLocaleString("en-IN")}` : "—"),
  },
];

const csvHeaders = [
  { label: "Material Name", key: "material_name" },
  { label: "Color", key: "color" },
  { label: "Sub Type", key: "sub_type" },
  { label: "Current Qty", key: "current_quantity" },
  { label: "Unit", key: "unit" },
  { label: "Rate Used", key: "rate_used" },
  { label: "Rate Type", key: "rate_type_label" },
  { label: "Valuation", key: "valuation" },
];

const InvRepValuation = () => {
  const [form] = Form.useForm();
  const [trigger, { data, isLoading, isFetching }] = useLazyFetchInventoryValuationReportQuery();

  const handleApply = () => {
    const v = form.getFieldsValue();
    const params = {};
    if (v.type) params.type = v.type;
    if (v.rate_type) params.rate_type = v.rate_type;
    if (v.color) params.color = v.color;
    trigger(params);
  };

  const handleClear = () => {
    form.resetFields();
    form.setFieldsValue({ rate_type: "avg" });
    trigger({ rate_type: "avg" });
  };

  const results = data?.results || [];
  const count = data?.count ?? results.length;
  const totalValuation = data?.total_valuation;
  const rateTypeLabel = data?.rate_type === "latest" ? "Latest Rate" : "Average Rate";

  return (
    <div style={{ padding: "24px" }}>
      <h2 style={{ marginBottom: 16 }}>Inventory Valuation Report</h2>

      <Collapse defaultActiveKey={["filters"]} style={{ marginBottom: 16 }}>
        <Panel header="Filters" key="filters">
          <Form
            form={form}
            layout="inline"
            initialValues={{ rate_type: "avg" }}
            style={{ flexWrap: "wrap", gap: "8px 0" }}
          >
            <Form.Item name="type" label="Material Type">
              <Select allowClear placeholder="All types" style={{ width: 180 }}>
                {MATERIAL_TYPES.map((t) => (
                  <Select.Option key={t} value={t}>{t}</Select.Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item name="rate_type" label="Rate Type">
              <Radio.Group>
                <Radio.Button value="avg">Average Rate</Radio.Button>
                <Radio.Button value="latest">Latest Rate</Radio.Button>
              </Radio.Group>
            </Form.Item>
            <Form.Item name="color" label="Color">
              <Input placeholder="Color" style={{ width: 120 }} />
            </Form.Item>
          </Form>
          <div style={{ marginTop: 12, display: "flex", gap: 8 }}>
            <Button type="primary" onClick={handleApply} loading={isLoading || isFetching}>
              Apply Filters
            </Button>
            <Button onClick={handleClear}>Clear Filters</Button>
          </div>
        </Panel>
      </Collapse>

      {data && (
        <div
          style={{
            display: "flex",
            gap: 24,
            marginBottom: 12,
            padding: "10px 16px",
            background: "#fafafa",
            borderRadius: 6,
            border: "1px solid #e8e8e8",
            flexWrap: "wrap",
            alignItems: "center",
          }}
        >
          <span><b>Total Records:</b> {count}</span>
          <span style={{ color: "#aaa" }}>|</span>
          <span><b>Rate Type:</b> {rateTypeLabel}</span>
          <span style={{ color: "#aaa" }}>|</span>
          <span>
            <b>Total Valuation:</b>{" "}
            {totalValuation != null
              ? `₹${Number(totalValuation).toLocaleString("en-IN")}`
              : "—"}
          </span>
          {results.length > 0 && (
            <>
              <span style={{ color: "#aaa" }}>|</span>
              <Tooltip title="Export to Excel">
                <CSVLink
                  data={results}
                  headers={csvHeaders}
                  filename="inventory-valuation-report.csv"
                >
                  <Button icon={<FileExcelOutlined />} style={{ color: "#217346", borderColor: "#217346" }}>
                    Export to Excel
                  </Button>
                </CSVLink>
              </Tooltip>
            </>
          )}
        </div>
      )}

      <Spin spinning={isLoading || isFetching}>
        <Table
          columns={columns}
          dataSource={results}
          rowKey={(r, i) => r.id ?? i}
          locale={{ emptyText: "No records found" }}
          scroll={{ x: true }}
          pagination={{ pageSize: 20, showSizeChanger: true }}
        />
      </Spin>
    </div>
  );
};

export default InvRepValuation;
